import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/query/queryKeys';
import { STALE } from '../../lib/query/staleTimes';
import { fetchMyGoingEvents, fetchMyRsvps } from '../../api/rsvps';

export function useMyRsvps(userId, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.rsvps.mine(userId),
    queryFn: () => fetchMyRsvps(userId),
    enabled: Boolean(userId) && enabled,
    staleTime: STALE.user,
  });
}

/** The events behind your going RSVPs, as feed rows (see fetchMyGoingEvents). */
export function useMyGoingEvents(rsvps, { enabled = true } = {}) {
  const ids = (rsvps ?? [])
    .filter((r) => r.status === 'going')
    .map((r) => r.event_id)
    .sort();
  return useQuery({
    queryKey: queryKeys.rsvps.goingEvents(ids),
    queryFn: () => fetchMyGoingEvents(ids),
    enabled: enabled && ids.length > 0,
    staleTime: STALE.user,
    // Joining or leaving changes the key; keep the rows still yours so the head of the list does not blink.
    placeholderData: (prev) => prev?.filter((e) => ids.includes(e.event_id)),
  });
}

/** Patch going/not-going on the shared mine-RSVP cache (optimistic UI). */
export function usePatchMyRsvps(userId) {
  const queryClient = useQueryClient();
  const key = queryKeys.rsvps.mine(userId);

  return (eventId, newStatus) => {
    if (!userId) return;
    queryClient.setQueryData(key, (prev = []) => {
      const filtered = prev.filter((r) => r.event_id !== eventId);
      return newStatus ? [...filtered, { event_id: eventId, status: newStatus }] : filtered;
    });
  };
}

export function useInvalidateMyRsvps() {
  const queryClient = useQueryClient();
  return (userId) => {
    if (!userId) return;
    queryClient.invalidateQueries({ queryKey: queryKeys.rsvps.mine(userId) });
  };
}

export function myRsvpMap(rsvps) {
  const map = {};
  (rsvps ?? []).forEach((r) => {
    map[r.event_id] = r.status;
  });
  return map;
}
